import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2,
  Stethoscope,
  Smile,
  Building,
  Landmark,
  GraduationCap,
  Store,
  Briefcase,
  ArrowRight
} from 'lucide-react';

const IndustriesGrid = () => {
  const industries = [
    {
      icon: Smile,
      name: 'Orthodontics',
      slug: 'orthodontics',
      description: 'Specialized IT networking solutions for orthodontic practices. Secure patient data management, HIPAA-compliant infrastructure, and seamless integration with practice management software.',
      services: [
        'HIPAA-Compliant Network Infrastructure',
        'Secure Patient Data Management',
        'Digital Imaging Network Setup',
        'Practice Management Software Integration',
        'Secure Cloud Backup Solutions',
        'Telehealth Infrastructure'
      ],
      challenges: [
        'Patient data privacy and HIPAA compliance',
        'Integration with digital imaging systems',
        'Reliable network for practice management software',
        'Secure remote access for staff'
      ],
      benefits: [
        'Enhanced patient data security',
        'Improved operational efficiency',
        'Seamless digital workflow',
        'Reduced IT downtime'
      ],
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      keywords: 'orthodontics IT support, dental practice network security, HIPAA compliant IT, orthodontic practice management software, dental imaging network'
    },
    {
      icon: Stethoscope,
      name: 'Dental Practices',
      slug: 'dental',
      description: 'Comprehensive IT solutions for dental clinics and practices. From network infrastructure to cybersecurity, ensuring your practice runs smoothly and securely.',
      services: [
        'Dental Practice Network Design',
        'EHR/EMR System Integration',
        'Digital X-Ray Network Configuration',
        'HIPAA Security & Compliance',
        'Cloud-Based Practice Management',
        'Secure Patient Portal Setup'
      ],
      challenges: [
        'Managing sensitive patient health information',
        'Ensuring HIPAA compliance across all systems',
        'Integrating multiple dental software systems',
        'Protecting against ransomware and cyber threats'
      ],
      benefits: [
        'Full HIPAA compliance assurance',
        'Streamlined practice operations',
        'Enhanced patient experience',
        'Protected against data breaches'
      ],
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      keywords: 'dental practice IT services, dental office network setup, HIPAA compliance dental, dental cybersecurity, dental practice management IT'
    },
    {
      icon: Landmark,
      name: 'Banking & Finance',
      slug: 'banking',
      description: 'High-security IT networking and cybersecurity solutions for financial institutions. Meeting strict regulatory requirements while ensuring optimal performance.',
      services: [
        'Financial Grade Network Security',
        'PCI-DSS Compliance Solutions',
        'Multi-Factor Authentication Systems',
        'Intrusion Detection & Prevention',
        'Secure Transaction Processing',
        'Regulatory Compliance Management'
      ],
      challenges: [
        'Meeting stringent regulatory requirements',
        'Protecting against sophisticated cyber attacks',
        'Ensuring 24/7 system availability',
        'Managing secure remote banking infrastructure'
      ],
      benefits: [
        'Bank-grade security infrastructure',
        'Full regulatory compliance',
        'Zero-downtime architecture',
        'Advanced threat protection'
      ],
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      keywords: 'banking IT security, financial institution network, PCI-DSS compliance, banking cybersecurity solutions, secure banking infrastructure'
    },
    {
      icon: Building,
      name: 'Healthcare',
      slug: 'healthcare',
      description: 'Secure and compliant IT infrastructure for healthcare providers. Supporting clinical workflows while maintaining the highest security standards.',
      services: [
        'Healthcare Network Infrastructure',
        'Medical Device Integration',
        'Telemedicine Platform Setup',
        'HIPAA Compliance Auditing',
        'Electronic Health Records Security',
        'Medical Imaging Network (PACS)'
      ],
      challenges: [
        'Protecting electronic health records',
        'Ensuring continuous system availability',
        'Managing IoMT (Internet of Medical Things) security',
        'Compliance with multiple healthcare regulations'
      ],
      benefits: [
        'Patient data protection',
        'Improved care coordination',
        'Regulatory compliance',
        'Enhanced clinical efficiency'
      ],
      color: 'text-red-500',
      bgColor: 'bg-red-50',
      keywords: 'healthcare IT solutions, hospital network security, medical practice IT support, HIPAA compliant infrastructure, healthcare cybersecurity'
    },
    {
      icon: GraduationCap,
      name: 'Education',
      slug: 'education',
      description: 'Reliable and secure IT networking for educational institutions. Supporting modern learning environments with robust infrastructure.',
      services: [
        'Campus-Wide Network Infrastructure',
        'Student Data Protection',
        'E-Learning Platform Integration',
        'WiFi for Large Venues',
        'Access Control Systems',
        'Cybersecurity Training Programs'
      ],
      challenges: [
        'Securing student and staff data',
        'Managing large-scale WiFi deployments',
        'Supporting diverse devices and platforms',
        'Budget constraints with high expectations'
      ],
      benefits: [
        'Secure learning environment',
        'Scalable infrastructure',
        'Enhanced connectivity',
        'Protected student information'
      ],
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
      keywords: 'education IT networking, school network infrastructure, campus cybersecurity, educational institution IT support, student data protection'
    },
    {
      icon: Store,
      name: 'Retail',
      slug: 'retail',
      description: 'Innovative IT solutions for retail businesses. From point-of-sale systems to inventory management, keeping your operations running smoothly.',
      services: [
        'POS System Network Configuration',
        'Inventory Management Integration',
        'Customer WiFi Solutions',
        'Payment Card Industry Compliance',
        'Video Surveillance Integration',
        'Multi-Location Network Management'
      ],
      challenges: [
        'Securing payment card data',
        'Managing multi-location networks',
        'Ensuring POS system reliability',
        'Protecting customer information'
      ],
      benefits: [
        'PCI-DSS compliance',
        'Reduced transaction downtime',
        'Enhanced customer experience',
        'Centralized management'
      ],
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-50',
      keywords: 'retail IT solutions, retail network security, POS system support, retail cybersecurity, multi-store IT management'
    },
    {
      icon: Briefcase,
      name: 'Professional Services',
      slug: 'professional-services',
      description: 'Tailored IT networking and security solutions for law firms, accounting firms, and consulting practices. Protecting client confidentiality and ensuring productivity.',
      services: [
        'Client Data Protection',
        'Secure File Sharing Solutions',
        'Virtual Private Networks (VPN)',
        'Document Management Systems',
        'Email Security & Encryption',
        'Compliance Management'
      ],
      challenges: [
        'Maintaining client confidentiality',
        'Secure remote access for professionals',
        'Protecting sensitive documents',
        'Meeting industry-specific regulations'
      ],
      benefits: [
        'Client trust and confidence',
        'Secure collaboration',
        'Regulatory compliance',
        'Business continuity'
      ],
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50',
      keywords: 'professional services IT, law firm IT support, accounting firm network security, consulting firm cybersecurity, confidential data protection'
    },
    {
      icon: Building2,
      name: 'Small Business',
      slug: 'small-business',
      description: 'Affordable and scalable IT solutions for small businesses. Enterprise-grade security and reliability without the enterprise price tag.',
      services: [
        'Small Office Network Setup',
        'Cloud Solutions Implementation',
        'Cybersecurity Essentials',
        'Remote Work Infrastructure',
        'Managed IT Services',
        'Business Continuity Planning'
      ],
      challenges: [
        'Limited IT budget and resources',
        'Growing security threats',
        'Supporting remote workforce',
        'Scaling infrastructure with growth'
      ],
      benefits: [
        'Cost-effective solutions',
        'Enterprise-grade security',
        'Scalable infrastructure',
        'Peace of mind'
      ],
      color: 'text-teal-500',
      bgColor: 'bg-teal-50',
      keywords: 'small business IT solutions, affordable network security, small office IT support, small business cybersecurity, managed IT for small business'
    }
  ];

  return (
    <section className="section-padding bg-background">
      <div className="max-w-7xl mx-auto container-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            Industry <span className="text-gradient">Expertise</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Deep industry knowledge combined with technical excellence. We speak your language 
            and understand your unique IT networking and cybersecurity challenges.
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {industries.map((industry, index) => {
            const IconComponent = industry.icon;
            return (
              <div
                key={industry.slug}
                className="card-hover group animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="p-8">
                  {/* Icon */}
                  <div className={`w-16 h-16 ${industry.bgColor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className={`w-8 h-8 ${industry.color}`} />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-text-primary mb-4 group-hover:text-primary transition-colors">
                    {industry.name}
                  </h3>

                  <p className="text-text-secondary mb-6 leading-relaxed line-clamp-3">
                    {industry.description}
                  </p>

                  {/* Key Services Preview */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wide mb-3">
                      Key Services
                    </h4>
                    <ul className="space-y-2">
                      {industry.services.slice(0, 3).map((service) => (
                        <li key={service} className="flex items-start text-sm text-text-secondary">
                          <div className={`w-1.5 h-1.5 ${industry.color.replace('text-', 'bg-')} rounded-full mt-2 mr-3 flex-shrink-0`}></div>
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Learn More Button */}
                  <Link
                    to={`/industries/${industry.slug}`}
                    className="hidden inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors group-hover:translate-x-1 transform duration-200"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>

                  {/* SEO Keywords (hidden but indexed) */}
                  <div className="sr-only" aria-hidden="true">
                    {industry.keywords}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-tech rounded-2xl p-12 text-white text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-6">
            Don't See Your Industry Listed?
          </h3>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            We work with businesses across all sectors. Contact us to discuss your specific 
            IT networking and cybersecurity needs, and we'll create a custom solution for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-secondary bg-white text-primary hover:bg-blue-50">
              Contact Our Team
            </Link>
            <Link to="/services" className="btn-ghost text-white border-white hover:bg-white/10">
              View All Services
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustriesGrid;
